import { useCallback } from "react";
import { Button, Checkbox, Collapse, DatePicker, Input, Select, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import QuoteLineItem from "./QuoteLineItem";
import {
    QUOTE_CATEGORIES,
    QUOTE_CURRENCIES,
    createEmptyLine,
} from "../../utils/quoteDefaults";
import { calcLineSubtotal, formatMoney } from "../../utils/quoteFormat";
import { useTranslation } from "../../i18n";

const { Paragraph, Title } = Typography;

function QuoteForm({ formState, onChange, validationError }) {
    const { t } = useTranslation();

    const updateField = useCallback(
        (field, value) => {
            onChange({ ...formState, [field]: value });
        },
        [formState, onChange]
    );

    const updateCategoryLines = useCallback(
        (categoryKey, lines) => {
            onChange({
                ...formState,
                categories: {
                    ...formState.categories,
                    [categoryKey]: lines,
                },
            });
        },
        [formState, onChange]
    );

    const handleAddLine = useCallback(
        (categoryKey) => {
            const lines = formState.categories[categoryKey] || [];
            updateCategoryLines(categoryKey, [...lines, createEmptyLine()]);
        },
        [formState.categories, updateCategoryLines]
    );

    const handleUpdateLine = useCallback(
        (categoryKey, lineId, nextLine) => {
            const lines = (formState.categories[categoryKey] || []).map((line) =>
                line.id === lineId ? nextLine : line
            );
            updateCategoryLines(categoryKey, lines);
        },
        [formState.categories, updateCategoryLines]
    );

    const handleRemoveLine = useCallback(
        (categoryKey, lineId) => {
            const lines = formState.categories[categoryKey] || [];
            if (lines.length <= 1) {
                return;
            }
            updateCategoryLines(
                categoryKey,
                lines.filter((line) => line.id !== lineId)
            );
        },
        [formState.categories, updateCategoryLines]
    );

    const handleGlobalNoteChange = useCallback(
        (index, value) => {
            const nextNotes = [...formState.globalNotes];
            nextNotes[index] = value;
            updateField("globalNotes", nextNotes);
        },
        [formState.globalNotes, updateField]
    );

    const handleAddGlobalNote = useCallback(() => {
        updateField("globalNotes", [...formState.globalNotes, ""]);
    }, [formState.globalNotes, updateField]);

    const handleRemoveGlobalNote = useCallback(
        (index) => {
            if (formState.globalNotes.length <= 1) {
                return;
            }
            updateField(
                "globalNotes",
                formState.globalNotes.filter((_, noteIndex) => noteIndex !== index)
            );
        },
        [formState.globalNotes, updateField]
    );

    const categorySubtotals = QUOTE_CATEGORIES.map((key) => {
        const lines = formState.categories[key] || [];
        const subtotal = lines.reduce(
            (sum, line) => sum + calcLineSubtotal(line.unitPrice, line.quantity),
            0
        );
        return { key, subtotal };
    });

    const grandTotal = categorySubtotals.reduce((sum, category) => sum + category.subtotal, 0);

    const collapseItems = QUOTE_CATEGORIES.map((categoryKey, index) => {
        const lines = formState.categories[categoryKey] || [];
        const categorySubtotal =
            categorySubtotals.find((category) => category.key === categoryKey)?.subtotal || 0;

        return {
            key: categoryKey,
            label: (
                <div className="quote-form__collapse-label">
                    <span>
                        {index + 1}. {t(`quote.categories.${categoryKey}`)}
                    </span>
                    <span className="quote-form__collapse-subtotal">
                        {formatMoney(categorySubtotal, formState.currency)}
                    </span>
                </div>
            ),
            children: (
                <div className="quote-form__category">
                    {lines.map((line) => (
                        <QuoteLineItem
                            key={line.id}
                            line={line}
                            currency={formState.currency}
                            canRemove={lines.length > 1}
                            onChange={(nextLine) => handleUpdateLine(categoryKey, line.id, nextLine)}
                            onRemove={() => handleRemoveLine(categoryKey, line.id)}
                        />
                    ))}
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => handleAddLine(categoryKey)}
                        className="quote-form__add-line"
                    >
                        {t("quote.form.addLine")}
                    </Button>
                </div>
            ),
        };
    });

    return (
        <div className="quote-form">
            <div className="quote-form__section">
                <Title level={5}>{t("quote.form.basicInfo")}</Title>

                <div className="quote-form__field">
                    <label htmlFor="quote-project-name">{t("quote.form.projectName")}</label>
                    <Input
                        id="quote-project-name"
                        value={formState.projectName}
                        placeholder={t("quote.form.projectNamePlaceholder")}
                        onChange={(event) => updateField("projectName", event.target.value)}
                    />
                </div>

                <div className="quote-form__row">
                    <div className="quote-form__field">
                        <label>{t("quote.form.quoteDate")}</label>
                        <DatePicker
                            value={formState.quoteDate}
                            onChange={(value) => updateField("quoteDate", value)}
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                            inputReadOnly
                        />
                    </div>

                    <div className="quote-form__field">
                        <label>{t("quote.form.validUntil")}</label>
                        <DatePicker
                            value={formState.validUntil}
                            onChange={(value) => updateField("validUntil", value)}
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                            inputReadOnly
                            allowClear
                        />
                    </div>
                </div>

                <div className="quote-form__row">
                    <div className="quote-form__field">
                        <label>{t("quote.form.currency")}</label>
                        <Select
                            value={formState.currency}
                            onChange={(value) => updateField("currency", value)}
                            options={QUOTE_CURRENCIES.map((currency) => ({
                                value: currency,
                                label: currency,
                            }))}
                            style={{ width: "100%" }}
                        />
                    </div>

                    <div className="quote-form__field">
                        <label htmlFor="quote-portfolio-url">{t("quote.form.portfolioUrl")}</label>
                        <Input
                            id="quote-portfolio-url"
                            value={formState.portfolioUrl}
                            placeholder={t("quote.form.portfolioUrlPlaceholder")}
                            onChange={(event) => updateField("portfolioUrl", event.target.value)}
                        />
                    </div>
                </div>

                <div className="quote-form__field quote-form__field--checkbox">
                    <Checkbox
                        checked={formState.hideSubitemPrices}
                        onChange={(event) => updateField("hideSubitemPrices", event.target.checked)}
                    >
                        {t("quote.form.hideSubitemPrices")}
                    </Checkbox>
                </div>
            </div>

            <div className="quote-form__section">
                <Title level={5}>{t("quote.form.lineItems")}</Title>
                <Collapse
                    items={collapseItems}
                    defaultActiveKey={QUOTE_CATEGORIES}
                    className="quote-form__collapse"
                />
            </div>

            <div className="quote-form__section">
                <Title level={5}>{t("quote.form.globalNotes")}</Title>
                <Paragraph type="secondary" className="quote-form__hint">
                    {t("quote.form.globalNotesHint")}
                </Paragraph>

                <div className="quote-form__notes">
                    {formState.globalNotes.map((note, index) => (
                        <div key={`note-${index}`} className="quote-form__note-row">
                            <Input
                                value={note}
                                placeholder={t("quote.form.globalNotePlaceholder")}
                                onChange={(event) => handleGlobalNoteChange(index, event.target.value)}
                            />
                            {formState.globalNotes.length > 1 && (
                                <Button
                                    type="text"
                                    danger
                                    onClick={() => handleRemoveGlobalNote(index)}
                                >
                                    {t("quote.form.removeNote")}
                                </Button>
                            )}
                            {index === formState.globalNotes.length - 1 && (
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddGlobalNote}
                                    aria-label={t("quote.form.addNote")}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="quote-form__total-bar">
                <span>{t("quote.form.grandTotal")}</span>
                <strong>{formatMoney(grandTotal, formState.currency)}</strong>
            </div>

            {validationError && (
                <Paragraph type="danger" className="quote-form__error">
                    {validationError}
                </Paragraph>
            )}
        </div>
    );
}

export default QuoteForm;
